<template>
  <form class="text-left" @submit.prevent="handleSubmit">
    <h2 class="text-xl font-semibold uppercase tracking-wide text-mouse">
      {{ t('checkout/step/profile/address/title') }}
    </h2>
    <p class="mt-2 text-rhino">
      {{
        rejected
          ? t('checkout/step/profile/address/rejected')
          : t('checkout/step/profile/address/description')
      }}
    </p>

    <!-- Country before postal code, because it decides whether a postal code is
         asked for at all and which format it has to match. Postal code and city
         share the last row, so the pair that fails together sits together. -->
    <div class="mt-6 grid grid-cols-1 gap-x-4 sm:grid-cols-2">
      <form-text
        v-model="formData.firstname"
        required
        autocomplete="given-name"
        :label="`${t('frontend/first-name')}:`"
        :error="errorFor('firstname')"
        :input-id="'checkout-address__first-name'"
      />
      <form-text
        v-model="formData.lastname"
        required
        class="mt-6 sm:mt-0"
        autocomplete="family-name"
        :label="`${t('frontend/last-name')}:`"
        :error="errorFor('lastname')"
        :input-id="'checkout-address__last-name'"
      />
      <form-select
        v-model="formData.country"
        required
        class="mt-6 sm:col-span-2"
        value-key="code"
        label-key="name"
        :label="`${t('frontend/country')}:`"
        :data="availableCountries"
        :filter-items-fn="handleFilterCountries"
        :input-id="'checkout-address__country'"
        :disabled="availableCountries.length === 0"
        :is-valid="errorFor('country').length === 0"
      />
      <form-text
        v-model="formData.street"
        class="mt-6 sm:col-span-2"
        autocomplete="address-line1"
        :required="isFullAddressRequired"
        :label="`${t('checkout/step/profile/address/street')}:`"
        :error="errorFor('street')"
        :input-id="'checkout-address__street'"
      />
      <form-text
        v-if="showPostalCode"
        v-model="formData.postalCode"
        class="mt-6"
        autocomplete="postal-code"
        :required="isPostalCodeRequired"
        :label="`${t('frontend/postal-code')}:`"
        :error="errorFor('postalCode')"
        :input-id="'checkout-address__postal-code'"
      />
      <form-text
        v-model="formData.locality"
        class="mt-6"
        autocomplete="address-level2"
        :required="isFullAddressRequired"
        :label="`${t('frontend/city')}:`"
        :error="errorFor('locality')"
        :input-id="'checkout-address__city'"
      />
    </div>

    <p v-if="countriesFetchState === 'error'" class="mt-4 text-danger-fg">
      {{ t('general/error/text') }}
      <button
        type="button"
        class="underline cursor-pointer"
        @click="() => fetchCountries()"
      >
        {{ t('general/retry') }}
      </button>
    </p>

    <!-- The country control marks itself through is-valid but cannot render the
         message, so it is named here. It has to show on arrival too, when there
         is no submit error yet. -->
    <p v-if="countryError.length > 0" class="mt-4 text-danger-fg" role="alert">
      {{ countryError }}
    </p>

    <p v-if="submitError.length > 0" class="mt-4 text-danger-fg" role="alert">
      {{ t(submitError) }}
    </p>

    <!-- House convention: secondary escape on the left, primary CTA on the
         right, both the same height. -->
    <div
      v-if="rejected"
      class="mt-6 flex flex-wrap items-center justify-between gap-3"
    >
      <common-button
        variant="secondary"
        outline
        :to="backTo"
        size="custom"
        class="px-8 py-3"
      >
        {{ t('checkout/step/profile/address/back') }}
      </common-button>

      <common-button
        class="px-8 py-3"
        type="submit"
        size="custom"
        :disabled="isSubmitting || !isFormValid"
      >
        <template v-if="isSubmitting">
          {{ t('checkout/step/profile/address/saving') }}
        </template>
        <template v-else>
          {{ t('checkout/step/profile/address/submit') }}
        </template>
      </common-button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { Code } from '@connectrpc/connect';
import { captureException } from '@sentry/nuxt';
import { BadRequest } from 'gportal-grpc/google/rpc/error_details_pb';
import { UpdateBillingAddressRequest } from 'gportal-grpc/gportal/api/user/v1/requests_pb';
import { localizedErrorKeyIs } from '~/helpers/checkout';
import useFormValidation from '~/composables/useFormValidation';
import {
  ALWAYS_VALID_VALIDATOR_RULE,
  HAS_VALUE_VALIDATOR_RULE,
} from '~/composables/useFormValidation/rules';
import type { FormFieldValidationRuleSet } from '~/composables/useFormValidation/types';

defineOptions({ name: 'CheckoutProfileAddressForm' });

const emit = defineEmits<{ saved: [] }>();

const {
  rejected = false,
  rejectedField = '',
  backTo,
} = defineProps<{
  // Refused, the form owns its submit; otherwise the payment card drives it.
  rejected?: boolean;
  rejectedField?: string;
  backTo: string;
}>();

// Chargebee's parameter names for our fields. Anything unmapped falls back to the
// summary line, which is the previous behaviour rather than a worse one.
const CHARGEBEE_FIELD_TO_FORM = new Map<string, FormField>([
  ['first_name', 'firstname'],
  ['last_name', 'lastname'],
  ['billing_address[first_name]', 'firstname'],
  ['billing_address[last_name]', 'lastname'],
  ['billing_address[zip]', 'postalCode'],
  ['billing_address[city]', 'locality'],
  ['billing_address[line1]', 'street'],
  ['billing_address[country]', 'country'],
]);

// The label each field's error message is phrased around.
const FIELD_LABEL_KEY: Record<FormField, string> = {
  firstname: 'frontend/first-name',
  lastname: 'frontend/last-name',
  country: 'frontend/country',
  postalCode: 'frontend/postal-code',
  locality: 'frontend/city',
  street: 'checkout/step/profile/address/street',
};

const POSTAL_CODE_MISMATCH_KEY =
  'checkout/step/profile/address/error/postal_code_country';
const POSTAL_CODE_MISSING_KEY =
  'checkout/step/profile/address/error/postal_code_missing';

const { t } = useI18n();
const authStore = useAuthStore();
const $grpcUser = useGrpcUser();

const address = authStore.user?.address;

type BillingAddressFormData = {
  firstname: string;
  lastname: string;
  country: string;
  postalCode: string;
  locality: string;
  street: string;
};

type FormField = keyof BillingAddressFormData;

const formData = reactive<BillingAddressFormData>({
  firstname: authStore.user?.firstName ?? '',
  lastname: authStore.user?.lastName ?? '',
  country: address?.country ?? '',
  postalCode: address?.postalCode ?? '',
  locality: address?.locality ?? '',
  street: address?.street ?? '',
});

// What the account already holds, so an untouched form can be skipped.
const initialFormData: BillingAddressFormData = { ...formData };
const isDirty = computed(() =>
  (Object.keys(initialFormData) as FormField[]).some(
    (field) => formData[field] !== initialFormData[field],
  ),
);

const isSubmitting = ref(false);
const submitError = ref('');

// Fields the server refused. Kept apart from the rule-driven errors because they
// are not rule failures: nothing about the value says it is wrong, only the
// answer we got back.
const serverInvalidFields = ref<Partial<Record<FormField, string>>>({});

const {
  availableCountries,
  countriesWithPostalCode,
  countriesRequiringPostalCode,
  countriesRequiringFullAddress,
  countriesFetchState,
  fetchCountries,
  handleFilterCountries,
} = useBillingCountries();

// The country as the customer sees it in the select, for the postal-code
// message. Falls back to the code while the list is still loading.
const countryName = computed(
  () =>
    availableCountries.value.find((c) => c.code === formData.country)?.name ??
    formData.country,
);

// Matching on PermissionDenied alone would show "we cannot accept orders for this
// address" for any other denial.
const BLOCKED_ADDRESS_TRANSLATION_KEY = 'grpc/address_not_available';

// Offer the field wherever the country has a postal code at all, not only where
// it is required. If the list has not arrived we cannot tell, so show it: hiding
// it sends an empty postal code and strands the customer on a form they cannot
// satisfy.
const showPostalCode = computed(
  () =>
    countriesWithPostalCode.value.length === 0 ||
    countriesWithPostalCode.value.includes(formData.country),
);

const isFullAddressRequired = computed(() =>
  countriesRequiringFullAddress.value.includes(formData.country),
);

// The backend requires it for the full-address countries as well, so mirror
// that here rather than letting the customer submit into a server error.
const isPostalCodeRequired = computed(
  () =>
    showPostalCode.value &&
    (countriesRequiringPostalCode.value.includes(formData.country) ||
      isFullAddressRequired.value),
);

// The rules the backend applies, so the customer is told before the round trip.
// Reactive because what is required depends on the country they pick.
const validationRules = computed<
  FormFieldValidationRuleSet<BillingAddressFormData>
>(() => {
  return {
    firstname: HAS_VALUE_VALIDATOR_RULE,
    lastname: HAS_VALUE_VALIDATOR_RULE,
    country: HAS_VALUE_VALIDATOR_RULE,
    postalCode: isPostalCodeRequired.value
      ? HAS_VALUE_VALIDATOR_RULE
      : ALWAYS_VALID_VALIDATOR_RULE,
    locality: isFullAddressRequired.value
      ? HAS_VALUE_VALIDATOR_RULE
      : ALWAYS_VALID_VALIDATOR_RULE,
    street: isFullAddressRequired.value
      ? HAS_VALUE_VALIDATOR_RULE
      : ALWAYS_VALID_VALIDATOR_RULE,
  };
});

const { invalidFields, isFormValid } =
  useFormValidation<BillingAddressFormData>(formData, validationRules);

// Mark the refused field on arrival, so a customer sent back from the payment
// step sees which one Chargebee objected to rather than re-checking all six.
// A refused postal code marks the country with it: the value is rejected for
// being wrong *for that country*, so marking one of the two describes half the
// problem and sends the customer to re-check a field that looks correct.
watch(
  () => rejectedField,
  (field) => {
    const name = CHARGEBEE_FIELD_TO_FORM.get(field);
    if (name == null) {
      serverInvalidFields.value = {};

      return;
    }

    serverInvalidFields.value =
      name === 'postalCode'
        ? {
            postalCode: POSTAL_CODE_MISMATCH_KEY,
            country: POSTAL_CODE_MISMATCH_KEY,
          }
        : { [name]: 'form/validation/invalid_field' };
  },
  { immediate: true },
);

// A server verdict only describes the values it was given, so any edit retires it
// and leaves the rule-driven checks in charge.
watch(
  () => ({ ...formData }),
  () => {
    serverInvalidFields.value = {};
  },
);

// A server refusal outranks the rule check: it is the more specific answer.
function errorFor(field: FormField): string {
  const key = serverInvalidFields.value[field] ?? invalidFields.value[field];
  if (key == null || key.length === 0) return '';

  // Name the pair. "Postal code is invalid" is not what happened: the value is a
  // real postal code, it just does not belong to the country the customer picked.
  if (key === POSTAL_CODE_MISMATCH_KEY) {
    if (formData.postalCode.trim().length === 0) {
      return t(POSTAL_CODE_MISSING_KEY, { country: countryName.value });
    }

    return t(key, {
      postalCode: formData.postalCode,
      country: countryName.value,
    });
  }

  return t(key, { field: t(FIELD_LABEL_KEY[field]) });
}

// The postal-code field already names the pair, so repeat it here only when that field is hidden.
const countryError = computed(() =>
  serverInvalidFields.value.country === POSTAL_CODE_MISMATCH_KEY &&
  showPostalCode.value
    ? ''
    : errorFor('country'),
);

async function handleSubmit(): Promise<boolean> {
  if (isSubmitting.value) return false;

  // Not refused, no disabled button guards this, so the guard moves here.
  if (!isFormValid.value) {
    submitError.value = 'checkout/step/profile/address/error/invalid';

    return false;
  }

  isSubmitting.value = true;
  submitError.value = '';
  serverInvalidFields.value = {};

  const request = new UpdateBillingAddressRequest({
    firstname: formData.firstname,
    lastname: formData.lastname,
    country: formData.country,
    postalCode: showPostalCode.value ? formData.postalCode : '',
    locality: formData.locality,
    street: formData.street,
  });

  // The address is stored per region, so it goes to the region the customer is
  // buying in, not the deployment that happened to serve the page.
  const { error } = await $grpcUser.executeApiCallActive(
    'updateBillingAddress',
    request,
    authStore.getAuthorizationMetadata(),
  );

  isSubmitting.value = false;

  if (error != null) {
    applyError(error);

    return false;
  }

  // The address is already saved, so a failed re-read must not strand the
  // customer on a form with no way out.
  try {
    await authStore.fetchUser();
  } catch (fetchError) {
    captureException(fetchError, {
      extra: { context: 'ProfileAddressForm.fetchUser' },
    });
  }

  // Saved values are the new baseline, so a second Pay click does not resend them.
  Object.assign(initialFormData, formData);

  // Not refused, the payment card awaits the return value instead.
  if (rejected) emit('saved');

  return true;
}

// The payment card drives this form as part of its own submit.
defineExpose({ submit: handleSubmit, isDirty });

function applyError(error: {
  code?: Code;
  message?: string;
  details?: Array<{ type: string; value: Uint8Array }>;
}): void {
  if (
    error.code === Code.PermissionDenied &&
    localizedErrorKeyIs(error.details, BLOCKED_ADDRESS_TRANSLATION_KEY)
  ) {
    submitError.value = 'checkout/step/profile/address/error/blocked';

    return;
  }

  if (error.code === Code.InvalidArgument) {
    // The structured detail names the fields the validator rejected - decoded the same way
    // registration does (useSubmitData.ts), instead of parsing them back out of the English
    // developer message.
    const badRequestDetails = (error.details ?? []).filter(
      (detail) => detail.type === 'google.rpc.BadRequest',
    );
    for (const detail of badRequestDetails) {
      const badRequest = new BadRequest().fromBinary(detail.value);
      for (const violation of badRequest.fieldViolations) {
        const name = toCamelCase(violation.field) as FormField;
        if (!(name in formData)) continue;

        if (name === 'postalCode') {
          serverInvalidFields.value.postalCode = POSTAL_CODE_MISMATCH_KEY;
          serverInvalidFields.value.country = POSTAL_CODE_MISMATCH_KEY;
        } else {
          serverInvalidFields.value[name] = 'form/validation/invalid_field';
        }
      }
    }
    submitError.value = 'checkout/step/profile/address/error/invalid';

    return;
  }

  submitError.value = 'checkout/step/profile/address/error/generic';
}
</script>
